import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";
import { Bike, Car, ShieldCheck, Gauge, Award, MapPin, CheckCircle2, Loader2, ArrowRight, ScanLine } from "lucide-react";
import api from "../lib/api";
import { useContent } from "../context/ContentContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

const NAVY = "#0F2744";
const RIDER_IMG = "https://images.unsplash.com/photo-1598548841213-9cdbcdf8ec47?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const DRIVER_IMG = "https://images.unsplash.com/photo-1553782097-130fef5d3e27?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const MAP_IMG = "https://static.prod-images.emergentagent.com/jobs/51fe5950-e829-4696-b661-6142eda1be8b/images/d6dbaae201e609b24ee074593b6f9749de833de96e299cece4f6910b93159cfc.jpeg";
const LOOP_IMGS = [
  "https://images.unsplash.com/photo-1762342842947-57da5a00426d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920",
  "https://images.unsplash.com/photo-1630406144797-821be1f35d75?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920",
  "https://images.unsplash.com/photo-1764862024029-aafc09b47fb5?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920",
  DRIVER_IMG,
];

const fade = {
  hidden: { opacity: 0, y: 28 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

const Reveal = ({ children, className = "", delay = 0 }) => (
  <motion.div
    className={className}
    variants={fade}
    initial="hidden"
    whileInView="show"
    viewport={{ once: true, margin: "-60px" }}
    transition={{ delay }}
  >
    {children}
  </motion.div>
);

export default function Assessment() {
  const { t } = useContent();
  const [pathway, setPathway] = useState("");
  const [form, setForm] = useState({ name: "", postcode: "", email: "", phone: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const formRef = useRef(null);

  const areas = (t("assessment.areas.list", "") || "").split(",").map((s) => s.trim()).filter(Boolean);
  const covered = (t("assessment.areas.postcodes", "") || "").split(",").map((s) => s.trim().toUpperCase()).filter(Boolean);

  const outward = (form.postcode || "").toUpperCase().match(/^[A-Z]{1,2}/);
  const areaWarn = useMemo(() => {
    if (!form.postcode.trim() || covered.length === 0) return false;
    return !covered.includes(outward ? outward[0] : "");
  }, [form.postcode, covered, outward]);

  const scrollToForm = () => formRef.current?.scrollIntoView({ behavior: "smooth" });
  const pageUrl = (typeof window !== "undefined" ? window.location.origin : "") + "/assessment";

  const prompts = (t("assessment.kiosk.prompts", "") || "").split(",").map((s) => s.trim()).filter(Boolean);
  const ticker = (t("assessment.kiosk.ticker", "") || "").split(",").map((s) => s.trim()).filter(Boolean);
  const riderImg = t("img.assessment.rider", RIDER_IMG);
  const driverImg = t("img.assessment.driver", DRIVER_IMG);
  const mapImg = t("img.assessment.map", MAP_IMG);
  const loopImgs = [t("img.assessment.loop0", LOOP_IMGS[0]), t("img.assessment.loop1", LOOP_IMGS[1]), t("img.assessment.loop2", LOOP_IMGS[2]), driverImg];
  const [promptIdx, setPromptIdx] = useState(0);
  const [bgIdx, setBgIdx] = useState(0);
  const [panelIdx, setPanelIdx] = useState(0);

  useEffect(() => {
    if (prompts.length < 2) return;
    const id = setInterval(() => setPromptIdx((i) => (i + 1) % prompts.length), 3500);
    return () => clearInterval(id);
  }, [prompts.length]);

  useEffect(() => {
    const id = setInterval(() => setBgIdx((i) => (i + 1) % loopImgs.length), 6000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const id = setInterval(() => setPanelIdx((i) => (i + 1) % 2), 5000);
    return () => clearInterval(id);
  }, []);

  const choosePathway = (p) => { setPathway(p); setTimeout(scrollToForm, 80); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) { toast.error("Please add your name and email."); return; }
    setBusy(true);
    try {
      await api.post("/assessment", { ...form, pathway: pathway || "both" });
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally { setBusy(false); }
  };

  const benefits = [
    { icon: ShieldCheck, t: t("assessment.benefit1.title"), d: t("assessment.benefit1.text") },
    { icon: Gauge, t: t("assessment.benefit2.title"), d: t("assessment.benefit2.text") },
    { icon: MapPin, t: t("assessment.benefit3.title"), d: t("assessment.benefit3.text") },
    { icon: Award, t: t("assessment.benefit4.title"), d: t("assessment.benefit4.text") },
  ];
  const steps = [t("assessment.how.step1"), t("assessment.how.step2"), t("assessment.how.step3")];

  return (
    <div className="bg-white" data-testid="assessment-page">
      {/* HERO — big-screen attract mode */}
      <section className="relative min-h-[90vh] lg:min-h-screen flex items-center overflow-hidden" style={{ backgroundColor: NAVY }}>
        {/* Looping Ken-Burns background */}
        <div className="absolute inset-0">
          <AnimatePresence>
            <motion.img
              key={bgIdx}
              src={loopImgs[bgIdx]}
              alt=""
              className="absolute inset-0 h-full w-full object-cover"
              initial={{ opacity: 0, scale: 1.12 }}
              animate={{ opacity: 0.38, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ opacity: { duration: 1.6 }, scale: { duration: 6.5, ease: "linear" } }}
            />
          </AnimatePresence>
          <div className="absolute inset-0" style={{ background: `linear-gradient(115deg, ${NAVY} 38%, rgba(15,39,68,0.74) 100%)` }} />
        </div>

        <div className="relative z-10 w-full max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 pt-20 pb-16 lg:pt-28 lg:pb-20">
          <div className="grid lg:grid-cols-[1fr_auto] gap-10 lg:gap-14 items-center">
            {/* Left: message */}
            <motion.div className="text-left" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
              <div className="flex items-center gap-3 mb-6 lg:mb-8">
                <img src="/rospa-logo.png" alt="RoSPA Advanced Drivers and Riders" className="h-14 w-14 lg:h-20 lg:w-20 object-contain" />
                <span className="text-amber-400 font-semibold tracking-wide text-sm lg:text-lg uppercase">{t("assessment.hero.eyebrow")}</span>
              </div>

              {/* Rotating attention-grabbing prompt */}
              <div className="min-h-[4.5rem] lg:min-h-[10rem] xl:min-h-[12rem] mb-3" data-testid="kiosk-prompt">
                <AnimatePresence mode="wait">
                  <motion.h1
                    key={promptIdx}
                    className="text-white font-extrabold leading-[1.02] text-4xl sm:text-5xl lg:text-7xl xl:text-8xl"
                    initial={{ opacity: 0, y: 28 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -28 }}
                    transition={{ duration: 0.5, ease: "easeOut" }}
                  >
                    {prompts[promptIdx] || t("assessment.hero.title")}
                  </motion.h1>
                </AnimatePresence>
              </div>

              <p className="mt-4 lg:mt-6 text-slate-200 text-lg lg:text-2xl max-w-2xl leading-relaxed">{t("assessment.hero.subtitle")}</p>

              <div className="mt-8 lg:mt-10 flex flex-wrap items-center gap-4">
                <Button onClick={scrollToForm} className="bg-amber-500 hover:bg-amber-400 text-[#0F2744] font-bold text-base lg:text-xl px-7 lg:px-10 py-6 lg:py-8 rounded-xl lg:rounded-2xl" data-testid="hero-cta">
                  {t("assessment.hero.cta")} <ArrowRight className="h-5 w-5 lg:h-7 lg:w-7 ml-1.5" />
                </Button>
                <div className="flex items-center gap-2 text-slate-200 text-sm lg:text-lg">
                  <CheckCircle2 className="h-5 w-5 lg:h-7 lg:w-7 text-amber-400" /> Free · no obligation
                </div>
              </div>
            </motion.div>

            {/* Right: dynamically swapping panel — map <-> QR */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="justify-self-center lg:justify-self-end w-full max-w-sm lg:w-96"
              data-testid="kiosk-swap-panel"
            >
              <div className="relative rounded-3xl bg-white/95 backdrop-blur shadow-2xl ring-1 ring-white/20 overflow-hidden h-[26rem] lg:h-[30rem]">
                <AnimatePresence mode="wait">
                  {panelIdx === 0 ? (
                    <motion.button
                      key="map"
                      type="button"
                      onClick={() => setPanelIdx(1)}
                      className="absolute inset-0 flex flex-col p-4 lg:p-5 text-left"
                      initial={{ opacity: 0, x: 24 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -24 }}
                      transition={{ duration: 0.5 }}
                      data-testid="kiosk-map-slide"
                    >
                      <div className="flex items-center gap-2 text-[#0F2744] font-bold text-base lg:text-2xl mb-3">
                        <MapPin className="h-5 w-5 lg:h-7 lg:w-7 text-amber-500" />
                        {t("assessment.kiosk.maptitle")}
                      </div>
                      <div className="flex-1 min-h-0">
                        <img src={mapImg} alt="Areas we cover across the West Midlands and Staffordshire" className="w-full h-full object-contain rounded-2xl" />
                      </div>
                    </motion.button>
                  ) : (
                    <motion.button
                      key="qr"
                      type="button"
                      onClick={() => setPanelIdx(0)}
                      className="absolute inset-0 flex flex-col items-center justify-center p-6 lg:p-8 text-center"
                      initial={{ opacity: 0, x: 24 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -24 }}
                      transition={{ duration: 0.5 }}
                      data-testid="kiosk-qr-slide"
                    >
                      <div className="flex items-center justify-center gap-2 text-[#0F2744] font-bold text-lg lg:text-2xl">
                        <ScanLine className="h-6 w-6 lg:h-8 lg:w-8 text-amber-500" />
                        {t("assessment.kiosk.scan")}
                      </div>
                      <motion.div
                        className="mt-5 inline-block bg-white p-3 lg:p-4 rounded-2xl"
                        animate={{ scale: [1, 1.04, 1] }}
                        transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
                      >
                        <QRCodeCanvas value={pageUrl} size={240} fgColor={NAVY} level="M" includeMargin={false} className="w-full h-auto" />
                      </motion.div>
                      <p className="mt-4 text-xs lg:text-sm text-slate-500 font-medium">Point your phone camera here</p>
                    </motion.button>
                  )}
                </AnimatePresence>

                {/* Slide dots */}
                <div className="absolute bottom-3 inset-x-0 flex items-center justify-center gap-2 z-10">
                  {[0, 1].map((i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setPanelIdx(i)}
                      aria-label={i === 0 ? "Show coverage map" : "Show QR code"}
                      data-testid={`kiosk-dot-${i}`}
                      className={`h-2 rounded-full transition-all ${panelIdx === i ? "w-6 bg-amber-500" : "w-2 bg-slate-300"}`}
                    />
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Live-feel ticker strip */}
        {ticker.length > 0 && (
          <div className="absolute top-0 inset-x-0 z-20 bg-amber-500 overflow-hidden" data-testid="kiosk-ticker">
            <motion.div
              className="flex items-center gap-10 whitespace-nowrap py-2.5 lg:py-3.5"
              animate={{ x: ["0%", "-50%"] }}
              transition={{ duration: 24, repeat: Infinity, ease: "linear" }}
            >
              {[...ticker, ...ticker, ...ticker, ...ticker].map((item, i) => (
                <span key={i} className="inline-flex items-center gap-2.5 text-[#0F2744] font-bold text-sm lg:text-lg uppercase tracking-wide">
                  <CheckCircle2 className="h-4 w-4 lg:h-5 lg:w-5" /> {item}
                </span>
              ))}
            </motion.div>
          </div>
        )}
      </section>

      {/* PATHWAY */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-20">
        <Reveal className="text-center max-w-2xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-extrabold text-[#0F2744]">{t("assessment.pathways.title")}</h2>
          <p className="mt-3 text-slate-500 text-lg">{t("assessment.pathways.subtitle")}</p>
        </Reveal>
        <div className="mt-10 grid sm:grid-cols-2 gap-5">
          {[
            { key: "rider", icon: Bike, label: "Advanced Riding", img: riderImg },
            { key: "driver", icon: Car, label: "Advanced Driving", img: driverImg },
          ].map(({ key, icon: Icon, label, img }) => (
            <Reveal key={key}>
              <button
                onClick={() => choosePathway(key)}
                data-testid={`pathway-${key}`}
                className={`group relative w-full overflow-hidden rounded-3xl text-left h-60 ring-2 transition-all ${pathway === key ? "ring-amber-500" : "ring-transparent hover:ring-slate-300"}`}
              >
                <img src={img} alt={label} className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                <div className="absolute inset-0" style={{ background: "linear-gradient(0deg, rgba(15,39,68,0.9) 10%, rgba(15,39,68,0.25) 90%)" }} />
                <div className="relative z-10 h-full flex flex-col justify-end p-6 text-white">
                  <Icon className="h-9 w-9 text-amber-400 mb-2" />
                  <span className="text-2xl font-extrabold">{label}</span>
                  <span className="text-sm text-slate-200 mt-1 inline-flex items-center gap-1">Choose this <ArrowRight className="h-4 w-4" /></span>
                </div>
              </button>
            </Reveal>
          ))}
        </div>
      </section>

      {/* BENEFITS */}
      <section className="py-20" style={{ backgroundColor: "#F7F9FC" }}>
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <Reveal><h2 className="text-3xl sm:text-4xl font-extrabold text-[#0F2744] text-center">{t("assessment.benefits.title")}</h2></Reveal>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {benefits.map((b, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <div className="h-full rounded-2xl bg-white border border-slate-100 p-6 shadow-sm">
                  <div className="h-12 w-12 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: NAVY }}>
                    <b.icon className="h-6 w-6 text-amber-400" />
                  </div>
                  <h3 className="font-bold text-[#0F2744]">{b.t}</h3>
                  <p className="text-sm text-slate-500 mt-2 leading-relaxed">{b.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="max-w-5xl mx-auto px-5 sm:px-8 py-20">
        <Reveal><h2 className="text-3xl sm:text-4xl font-extrabold text-[#0F2744] text-center">{t("assessment.how.title")}</h2></Reveal>
        <div className="mt-12 grid sm:grid-cols-3 gap-6">
          {steps.map((s, i) => (
            <Reveal key={i} delay={i * 0.1}>
              <div className="text-center">
                <div className="mx-auto h-14 w-14 rounded-full flex items-center justify-center text-amber-400 text-xl font-extrabold border-2 border-amber-400">{i + 1}</div>
                <p className="mt-4 text-slate-600">{s}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* AREAS */}
      <section className="bg-[#0F2744] py-20" data-testid="assessment-closing">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <img src={mapImg} alt="Areas we cover across the West Midlands and Staffordshire" className="w-full rounded-3xl shadow-2xl" />
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white">{t("assessment.areas.title")}</h2>
            <p className="mt-4 text-slate-300 text-lg">{t("assessment.areas.text")}</p>
            <p className="mt-6 text-xs uppercase tracking-wide text-amber-400 font-semibold">Towns &amp; areas we train in</p>
            <div className="mt-2 flex flex-wrap gap-2" data-testid="areas-list">
              {areas.map((a) => (
                <span key={a} className="inline-flex items-center gap-1.5 rounded-full bg-white/10 text-slate-100 text-sm px-3 py-1.5">
                  <MapPin className="h-3.5 w-3.5 text-amber-400" />{a}
                </span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* REGISTER */}
      <section ref={formRef} id="register" className="max-w-5xl mx-auto px-5 sm:px-8 py-20 scroll-mt-20">
        <Reveal className="text-center max-w-2xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-extrabold text-[#0F2744]">{t("assessment.form.title")}</h2>
          <p className="mt-3 text-slate-500 text-lg">{t("assessment.form.subtitle")}</p>
        </Reveal>

        <div className="mt-10 grid lg:grid-cols-[1.4fr_1fr] gap-6 items-start">
          <Reveal className="rounded-3xl bg-white border border-slate-200 shadow-lg p-6 sm:p-8">
            {done ? (
              <div className="text-center py-10" data-testid="assessment-success">
                <CheckCircle2 className="h-14 w-14 text-green-600 mx-auto" />
                <h3 className="mt-4 text-2xl font-extrabold text-[#0F2744]">Thank you!</h3>
                <p className="mt-2 text-slate-500 max-w-md mx-auto">We've got your details and sent you a confirmation email. A local tutor will be in touch soon to arrange your free assessment.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-1">I'm interested in</label>
                  <div className="grid grid-cols-3 gap-2">
                    {[{ k: "rider", l: "Riding" }, { k: "driver", l: "Driving" }, { k: "both", l: "Both" }].map(({ k, l }) => (
                      <button type="button" key={k} onClick={() => setPathway(k)} data-testid={`form-pathway-${k}`}
                        className={`rounded-lg border-2 py-2 text-sm font-semibold transition-colors ${pathway === k ? "border-amber-500 bg-amber-50 text-[#0F2744]" : "border-slate-200 text-slate-500 hover:border-slate-300"}`}>
                        {l}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-600 mb-1">Full name</label>
                    <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" data-testid="assess-name" required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-600 mb-1">Postcode</label>
                    <Input value={form.postcode} onChange={(e) => setForm({ ...form, postcode: e.target.value })} placeholder="e.g. B1 1AA" data-testid="assess-postcode" />
                  </div>
                </div>
                {areaWarn && (
                  <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="area-warn">
                    We mainly cover the West Midlands &amp; Staffordshire. Leave your details anyway — we'll let you know if we can help or as we expand.
                  </p>
                )}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-600 mb-1">Email</label>
                    <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@example.com" data-testid="assess-email" required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-600 mb-1">Phone</label>
                    <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Mobile number" data-testid="assess-phone" />
                  </div>
                </div>
                <Button type="submit" disabled={busy} className="w-full bg-[#0F2744] hover:bg-[#0B192C] text-white font-bold py-6 rounded-xl text-base" data-testid="assess-submit">
                  {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Request my free assessment"}
                </Button>
                <p className="text-xs text-slate-400 text-center">{t("assessment.form.reassure")}</p>
              </form>
            )}
          </Reveal>

          <Reveal delay={0.1} className="rounded-3xl p-6 sm:p-8 text-center" >
            <div className="rounded-3xl border border-slate-200 bg-slate-50 p-6">
              <p className="font-semibold text-[#0F2744]">{t("assessment.qr.title")}</p>
              <div className="mt-4 inline-block bg-white p-3 rounded-2xl shadow-sm" data-testid="assessment-qr">
                <QRCodeCanvas value={pageUrl} size={168} fgColor={NAVY} level="M" includeMargin={false} />
              </div>
              <p className="mt-3 text-xs text-slate-400 break-all">{pageUrl}</p>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
